/**
 * Admin Credit Management Page
 * Allows admins to manage credits for manufacturers
 */

import React, { useEffect, useState } from 'react';
import { creditService, ManufacturerWithBalance, CreditTransaction } from '../services/credit.service';
import { PageHeader, StatusBadge } from '../components/ui';
import { mapStatusToBadge, mapStatusToTone } from '../lib/status';

const AdminCreditManagementPageContent: React.FC = () => {
  const [manufacturers, setManufacturers] = useState<ManufacturerWithBalance[]>([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState<ManufacturerWithBalance | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions'>('overview');

  // Credit operation states
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);

  useEffect(() => {
    loadManufacturers();
  }, []);

  useEffect(() => {
    if (selectedManufacturer && activeTab === 'transactions') {
      loadTransactions();
    }
  }, [selectedManufacturer, activeTab]);

  const loadManufacturers = async () => {
    try {
      setLoading(true);
      const result = await creditService.getAllManufacturersWithBalances();
      
      if (result.success) {
        setManufacturers(result.manufacturers || []);
        setError(null);
      } else {
        setError(result.error || 'Failed to load manufacturers');
      }
    } catch (err) {
      setError('Failed to load manufacturers');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    if (!selectedManufacturer) return;

    try {
      const result = await creditService.getManufacturerTransactionHistory(
        selectedManufacturer.id,
        { limit: 50 }
      );
      
      if (result.success) {
        setTransactions(result.transactions || []);
      } else {
        setError(result.error || 'Failed to load transactions');
      }
    } catch (err) {
      setError('Failed to load transactions');
    }
  };

  const handleLoadCredits = async (amount: number, description: string) => {
    if (!selectedManufacturer) return;

    try {
      setOperationLoading(true);
      const result = await creditService.loadCredits(
        selectedManufacturer.id,
        creditService.parseAmount(amount),
        description
      );

      if (result.success) {
        setShowLoadModal(false);
        await loadManufacturers();
        // Update selected manufacturer
        const updated = manufacturers.find(m => m.id === selectedManufacturer.id);
        if (updated) {
          setSelectedManufacturer({ ...updated, creditBalance: updated.creditBalance + creditService.parseAmount(amount) });
        }
      } else {
        setError(result.error || 'Failed to load credits');
      }
    } catch (err) {
      setError('Failed to load credits');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleRefundCredits = async (amount: number, reason: string) => {
    if (!selectedManufacturer) return;

    try {
      setOperationLoading(true);
      const result = await creditService.refundCredits(
        selectedManufacturer.id,
        creditService.parseAmount(amount),
        reason
      );

      if (result.success) {
        setShowRefundModal(false);
        await loadManufacturers();
        // Update selected manufacturer - refund should subtract from balance
        const updated = manufacturers.find(m => m.id === selectedManufacturer.id);
        if (updated) {
          setSelectedManufacturer({ ...updated, creditBalance: updated.creditBalance - creditService.parseAmount(amount) });
        }
      } else {
        setError(result.error || 'Failed to refund credits');
      }
    } catch (err) {
      setError('Failed to refund credits');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleAdjustCredits = async (amount: number, reason: string) => {
    if (!selectedManufacturer) return;

    try {
      setOperationLoading(true);
      const result = await creditService.adjustCredits(
        selectedManufacturer.id,
        creditService.parseAmount(amount),
        reason
      );

      if (result.success) {
        setShowAdjustModal(false);
        await loadManufacturers();
        // Update selected manufacturer
        const updated = manufacturers.find(m => m.id === selectedManufacturer.id);
        if (updated) {
          setSelectedManufacturer({ ...updated, creditBalance: updated.creditBalance + creditService.parseAmount(amount) });
        }
      } else {
        setError(result.error || 'Failed to adjust credits');
      }
    } catch (err) {
      setError('Failed to adjust credits');
    } finally {
      setOperationLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-20 bg-gray-200 rounded"></div>
                ))}
              </div>
              <div className="lg:col-span-2 h-96 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <PageHeader title="Credit Management" description="Manage credits for manufacturers" />
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
            <button
              onClick={() => setError(null)}
              className="mt-2 text-xs text-red-700 hover:text-red-900 underline"
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Manufacturer List */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Manufacturers</h2>
              <p className="text-sm text-gray-600">Select a manufacturer to manage credits</p>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              {manufacturers.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No manufacturers found
                </div>
              ) : (
                <div className="space-y-1">
                  {manufacturers.map((manufacturer) => {
                    const statusBadge = mapStatusToBadge(manufacturer.isApproved ? 'approved' : 'pending');
                    return (
                      <button
                        key={manufacturer.id}
                        onClick={() => setSelectedManufacturer(manufacturer)}
                        className={`w-full text-left p-4 hover:bg-gray-50 border-b border-gray-100 ${
                          selectedManufacturer?.id === manufacturer.id ? 'bg-blue-50 border-blue-200' : ''
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {manufacturer.name}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {manufacturer.contactEmail}
                            </p>
                            <div className="flex items-center mt-1">
                              <StatusBadge tone={statusBadge.tone} size="sm">
                                {statusBadge.label}
                              </StatusBadge>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-sm font-semibold ${
                              manufacturer.creditBalance > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {creditService.formatAmount(manufacturer.creditBalance)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {manufacturer.totalBatches} batches
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Credit Management Panel */}
          <div className="lg:col-span-2">
            {selectedManufacturer ? (
              <div className="bg-white rounded-lg shadow">
                {/* Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {selectedManufacturer.name}
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        {selectedManufacturer.contactEmail}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Current Balance</p>
                      <p className={`text-2xl font-bold ${
                        selectedManufacturer.creditBalance > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {creditService.formatAmount(selectedManufacturer.creditBalance)}
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => setShowLoadModal(true)}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                    >
                      Load Credits
                    </button>
                    <button
                      onClick={() => setShowRefundModal(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                    >
                      Refund Credits
                    </button>
                    <button
                      onClick={() => setShowAdjustModal(true)}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm"
                    >
                      Adjust Credits
                    </button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200">
                  <nav className="flex -mb-px">
                    <button
                      onClick={() => setActiveTab('overview')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 ${
                        activeTab === 'overview'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Overview
                    </button>
                    <button
                      onClick={() => setActiveTab('transactions')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 ${
                        activeTab === 'transactions'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Transaction History
                    </button>
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {activeTab === 'overview' ? (
                    <div className="space-y-6">
                      {/* Stats */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm font-medium text-gray-600">Total Transactions</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {selectedManufacturer.totalTransactions}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm font-medium text-gray-600">Total Batches</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {selectedManufacturer.totalBatches}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm font-medium text-gray-600">Status</p>
                          <StatusBadge
                            tone={mapStatusToTone(selectedManufacturer.isApproved ? 'approved' : 'pending')}
                            size="md"
                          >
                            {selectedManufacturer.isApproved ? 'Approved' : 'Pending Approval'}
                          </StatusBadge>
                        </div>
                      </div>

                      {/* Account Info */}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Account Information</h3>
                        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                            <dd className="text-sm text-gray-900">{selectedManufacturer.name}</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Contact Email</dt>
                            <dd className="text-sm text-gray-900">{selectedManufacturer.contactEmail}</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Account Created</dt>
                            <dd className="text-sm text-gray-900">
                              {new Date(selectedManufacturer.createdAt).toLocaleDateString()}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Current Balance</dt>
                            <dd className={`text-sm font-semibold ${
                              selectedManufacturer.creditBalance > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {creditService.formatAmount(selectedManufacturer.creditBalance)}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Transactions</h3>
                      {transactions.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-gray-500">No transactions found</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {transactions.map((transaction) => (
                            <div key={transaction.id} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {creditService.getTransactionTypeDisplay(transaction.transactionType)}
                                  </p>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {transaction.description}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-2">
                                    {new Date(transaction.createdAt).toLocaleString()}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className={`text-sm font-semibold ${
                                    transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {transaction.amount > 0 ? '+' : ''}{creditService.formatAmount(Math.abs(transaction.amount))}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Balance: {creditService.formatAmount(transaction.balanceAfter)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                <p className="text-gray-500">Select a manufacturer to manage their credits</p>
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        <CreditOperationModal
          isOpen={showLoadModal}
          onClose={() => setShowLoadModal(false)}
          onSubmit={handleLoadCredits}
          title="Load Credits"
          submitLabel="Load Credits"
          loading={operationLoading}
          showDescription={true}
        />

        <CreditOperationModal
          isOpen={showRefundModal}
          onClose={() => setShowRefundModal(false)}
          onSubmit={handleRefundCredits}
          title="Refund Credits"
          submitLabel="Refund Credits"
          loading={operationLoading}
          showDescription={false}
          reasonLabel="Refund Reason"
        />

        <CreditOperationModal
          isOpen={showAdjustModal}
          onClose={() => setShowAdjustModal(false)}
          onSubmit={handleAdjustCredits}
          title="Adjust Credits"
          submitLabel="Adjust Credits"
          loading={operationLoading}
          showDescription={false}
          reasonLabel="Adjustment Reason"
          allowNegative={true}
        />
      </div>
    </div>
  );
};

// Credit Operation Modal Component
interface CreditOperationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (amount: number, reason: string) => void;
  title: string;
  submitLabel: string;
  loading: boolean;
  showDescription?: boolean;
  reasonLabel?: string;
  allowNegative?: boolean;
}

const CreditOperationModal: React.FC<CreditOperationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  submitLabel,
  loading,
  showDescription = false,
  reasonLabel = 'Reason',
  allowNegative = false,
}) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || (!allowNegative && numAmount <= 0)) {
      return;
    }

    if (!reason.trim()) {
      return;
    }

    onSubmit(numAmount, reason.trim());
  };

  const handleClose = () => {
    setAmount('');
    setReason('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount (INR)
            </label>
            <input
              type="number"
              step="0.01"
              min={allowNegative ? undefined : "0.01"}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
              style={{ color: '#111827' }}
              placeholder="Enter amount"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {reasonLabel}
            </label>
            {showDescription ? (
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                style={{ color: '#111827' }}
                placeholder="Optional description"
              />
            ) : (
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                style={{ color: '#111827' }}
                rows={3}
                placeholder="Enter reason"
                required
              />
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !amount || (!showDescription && !reason.trim())}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Processing...' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminCreditManagementPage: React.FC = () => <AdminCreditManagementPageContent />;

export default AdminCreditManagementPage;
